<template>
  <b-modal
    header-close-content=""
    v-model="isOpen"
    @close="hideDialog"
    @cancel="hideDialog"
    :title="computedTitle"
    size="xl"
    :id="modalName"
    body-class="manage-catalogs-modal"
    ref="modal"
    ok-only
    :ok-title="$t('close')"
    @show="fetchData"
    @hide="handleModalHide"
    @ok="openCatalog"
    :ok-disabled="true"
    footer-class="manage-catalogs-modal-footer"
    data-cy="channel-creator-manage-catalogs-modal">
    <template #default="{ close }">
      <div class="manipulation-area mb-3" data-cy="catalogs-manipulation-area">
        <div class="button-area" v-if="dialogMode == 'manage'">
          <b-btn class="mono btn-with-icon" :disabled="isDeleteDisabled" @click="handleMultipleDelete"> <i class="mdp-Delete"></i> {{ $t("delete") }} </b-btn>

          <b-btn class="mono btn-with-icon" :disabled="isRenameDisabled" v-b-modal.rename-manage-catalog-dialog>
            <i class="mdp-Rename"></i> {{ $t("Rename") }}
          </b-btn>
          <rename-catalog-dialog
            @renamed="catalogsHaveChanged"
            modalName="rename-manage-catalog-dialog"
            :selected-id="selectedSingleCatalogId"
            :selected-catalog="multiLangCatalogModel" />

          <b-dropdown
            @click="restoreSnapShot"
            class="mx-2"
            toggle-class="mono"
            split-class="mono"
            v-if="isASnapshotInSelection"
            :disabled="isRestoreDisabled"
            split
            data-cy="catalog-snapshot-restore-ddn">
            <template #button-content> <i class="mdp-Restore"></i> {{ $t("Restore") }} </template>
            <b-dropdown-item :disabled="isRestoreAsCopyDisabled" @click="restorAsCopySnapShot">{{ $t("RestoreAsCopy") }}</b-dropdown-item>
          </b-dropdown>
          <b-btn class="mono btn-with-icon" :disabled="isSnapshotDisabled" v-b-modal.versioning-comment-modal v-else data-cy="catalog-versioning-btn">
            <i class="mdp-SnapshotCreate"></i> {{ $t("SnapshotCatalog") }}
          </b-btn>
          <versioning-comment-modal
            :selected-catalog="multiLangCatalogModel"
            :language="languageCode"
            @ok="handleCreationOfSnapshot_OK"
            @error="handleCreationOfSnapshot_Error"
            data-cy="catalog-versioning-snapshot-modal" />
        </div>
        <div class="catalog-search mr-3">
          <div class="filter-search input-group">
            <div class="input-group-prepend">
              <div class="input-group-text">
                <i class="mdp-Filter"></i>
              </div>
            </div>
            <b-input :placeholder="$t('FilterByCatalogName')" v-model="searchTerm" />
            <div class="input-group-append" v-if="searchTerm">
              <b-button @click="emptySearchTerm">
                <i class="mdp-Close"></i>
              </b-button>
            </div>
          </div>
        </div>
      </div>

      <div class="catalog-table" v-if="fetchedData" data-cy="manage-catalog-table">
        <div class="catalog-table-header">
          <div class="catalog-table-col options-column" v-if="dialogMode == 'manage'">
            <b-checkbox v-model="selectAll" />
          </div>
          <div class="catalog-table-col options-column" v-if="dialogMode == 'manage'"></div>
          <div class="catalog-table-col name-col" :class="{ 'sorted-by': sortBy.name == 'category' }" @click="setSortBy('category')">
            <div>{{ $t("category") }} / {{ $t("name") }}</div>
            <div class="ml-2" v-if="sortBy.name == 'category'">
              <i class="mdp-ChevronBottom font-weight-bolder" v-if="!sortBy.desc" />
              <i class="mdp-ChevronTop font-weight-bolder" v-if="sortBy.desc" />
            </div>
          </div>
          <div class="catalog-table-col value-col" :class="{ 'sorted-by': sortBy.name == 'catalogArticles' }" @click="setSortBy('catalogArticles')">
            {{ $t("Articles") }}
            <span class="ml-2" v-if="sortBy.name == 'catalogArticles'">
              <i class="mdp-ChevronBottom font-weight-bolder" v-if="!sortBy.desc" />
              <i class="mdp-ChevronTop font-weight-bolder" v-if="sortBy.desc" />
            </span>
          </div>
          <div class="catalog-table-col value-col" :class="{ 'sorted-by': sortBy.name == 'variationGroups' }" @click="setSortBy('variationGroups')">
            {{ $t("VariantGroups") }}
            <span class="ml-2" v-if="sortBy.name == 'variationGroups'">
              <i class="mdp-ChevronBottom font-weight-bolder" v-if="!sortBy.desc" />
              <i class="mdp-ChevronTop font-weight-bolder" v-if="sortBy.desc" />
            </span>
          </div>
          <div class="catalog-table-col value-col" :class="{ 'sorted-by': sortBy.name == 'catalogGroups' }" @click="setSortBy('catalogGroups')">
            {{ $t("CatalogGroups") }}
            <span class="ml-2" v-if="sortBy.name == 'catalogGroups'">
              <i class="mdp-ChevronBottom font-weight-bolder" v-if="!sortBy.desc" />
              <i class="mdp-ChevronTop font-weight-bolder" v-if="sortBy.desc" />
            </span>
          </div>
          <div class="catalog-table-col value-col" :class="{ 'sorted-by': sortBy.name == 'updatedOn' }" @click="setSortBy('updatedOn')">
            {{ $t("UpdatedOn") }}
            <span class="ml-2" v-if="sortBy.name == 'updatedOn'">
              <i class="mdp-ChevronBottom font-weight-bolder" v-if="!sortBy.desc" />
              <i class="mdp-ChevronTop font-weight-bolder" v-if="sortBy.desc" />
            </span>
          </div>
          <div class="catalog-table-col value-col" :class="{ 'sorted-by': sortBy.name == 'updatedBy' }" @click="setSortBy('updatedBy')">
            {{ $t("UpdatedBy") }}
            <span class="ml-2" v-if="sortBy.name == 'updatedBy'">
              <i class="mdp-ChevronBottom font-weight-bolder" v-if="!sortBy.desc" />
              <i class="mdp-ChevronTop font-weight-bolder" v-if="sortBy.desc" />
            </span>
          </div>
          <div class="catalog-table-col value-col" :class="{ 'sorted-by': sortBy.name == 'createdOn' }" @click="setSortBy('createdOn')">
            {{ $t("CreatedOn") }}
            <span class="ml-2" v-if="sortBy.name == 'createdOn'">
              <i class="mdp-ChevronBottom font-weight-bolder" v-if="!sortBy.desc" />
              <i class="mdp-ChevronTop font-weight-bolder" v-if="sortBy.desc" />
            </span>
          </div>
          <div class="catalog-table-col value-col" :class="{ 'sorted-by': sortBy.name == 'createdBy' }" @click="setSortBy('createdBy')">
            {{ $t("CreatedBy") }}
            <span class="ml-2" v-if="sortBy.name == 'createdBy'">
              <i class="mdp-ChevronBottom font-weight-bolder" v-if="!sortBy.desc" />
              <i class="mdp-ChevronTop font-weight-bolder" v-if="sortBy.desc" />
            </span>
          </div>
        </div>
        <div v-for="catalog in sortedList" :key="catalog.catalogId" class="catalog-table-row">
          <div class="catalog-table-row-container">
            <div class="catalog-table-row-data" :class="{ active: catalog.selected }">
              <div class="catalog-table-col options-column" v-if="dialogMode == 'manage'" data-cy="catalog-table-checkbox-options-column">
                <b-checkbox v-model="catalog.selected" @change="deselectAllSnapshots" data-cy="catalog-snapshot-cbx" />
              </div>
              <div class="catalog-table-col options-column" v-if="dialogMode == 'manage'" data-cy="catalog-table-snapshot-options-column">
                <div class="snapshot-expander mr-2" v-if="hasSnapshot(catalog.catalogId)">
                  <span
                    v-if="!catalog.isShowingCollapsedSnapshots"
                    @click="catalog.isShowingCollapsedSnapshots = true"
                    data-cy="catalog-table-snapshot-options-column-expander">
                    <i class="mdp-ChevronRight" />
                  </span>
                  <span v-if="catalog.isShowingCollapsedSnapshots" @click="catalog.isShowingCollapsedSnapshots = false">
                    <i class="mdp-ChevronBottom" />
                  </span>
                </div>
              </div>
              <div
                class="catalog-table-col name-col"
                :id="catalog.catalogId"
                @dblclick="openDblClickCatalog(catalog.catalogId, close)"
                @click="handleSelectClickCatalog($event, catalog)">
                <span>{{ catalog.category }} / {{ catalog.name }}</span>
              </div>
              <div
                class="catalog-table-col value-col"
                @dblclick="openDblClickCatalog(catalog.catalogId, close)"
                @click="handleSelectClickCatalog($event, catalog)">
                {{ catalog.catalogArticles }}<span v-if="catalog.linkedCatalogNodes > 0">+</span>
              </div>
              <div
                class="catalog-table-col value-col"
                @dblclick="openDblClickCatalog(catalog.catalogId, close)"
                @click="handleSelectClickCatalog($event, catalog)">
                {{ catalog.variationGroups }}<span v-if="catalog.linkedCatalogNodes > 0">+</span>
              </div>
              <div
                class="catalog-table-col value-col"
                @dblclick="openDblClickCatalog(catalog.catalogId, close)"
                @click="handleSelectClickCatalog($event, catalog)">
                {{ catalog.catalogGroups }}<span v-if="catalog.linkedCatalogNodes > 0">+</span>
              </div>
              <div
                class="catalog-table-col value-col"
                @dblclick="openDblClickCatalog(catalog.catalogId, close)"
                @click="handleSelectClickCatalog($event, catalog)">
                {{ $d(catalog.updatedOn, "shortDateTime") }}
              </div>
              <div
                class="catalog-table-col value-col"
                @dblclick="openDblClickCatalog(catalog.catalogId, close)"
                @click="handleSelectClickCatalog($event, catalog)">
                {{ catalog.updatedBy }}
              </div>
              <div
                class="catalog-table-col value-col"
                @dblclick="openDblClickCatalog(catalog.catalogId, close)"
                @click="handleSelectClickCatalog($event, catalog)">
                {{ $d(catalog.createdOn, "shortDateTime") }}
              </div>
              <div
                class="catalog-table-col value-col"
                @dblclick="openDblClickCatalog(catalog.catalogId, close)"
                @click="handleSelectClickCatalog($event, catalog)">
                {{ catalog.createdBy }}
              </div>
            </div>
            <div class="catalog-table-row-snapshot" v-if="catalog.isShowingCollapsedSnapshots" data-cy="catalog-table-row-snapshot">
              <div
                class="catalog-table-row-snapshot-entry"
                v-for="snapshot in sortSnapshots(snapShots ? snapShots.find((x) => x.catalogId == catalog.catalogId) : null)"
                :key="snapshot.catalogId"
                :class="{ active: snapshot.selected }">
                <div class="catalog-table-col options-column" v-if="dialogMode == 'manage'" data-cy="catalog-table-snapshot-entry-options-column">
                  <b-checkbox @change="deselectAllCatalogs" v-model="snapshot.selected" data-cy="catalog-snapshot-entry-cbx" />
                </div>
                <div class="catalog-table-col options-column" v-if="dialogMode == 'manage'"></div>
                <div class="catalog-table-col name-col pl-5" @click="handleSelectClickSnapshot($event, snapshot)">
                  <span v-if="snapshot.remarks">{{ snapshot.remarks }}</span>
                  <span class="font-italic text-black-50" v-else>{{ $t("NoRemarks") }}</span>
                </div>
                <div class="catalog-table-col value-col" @click="handleSelectClickSnapshot($event, snapshot)">
                  {{ snapshot.articles }}
                </div>
                <div class="catalog-table-col value-col" @click="handleSelectClickSnapshot($event, snapshot)">
                  {{ snapshot.variationGroups }}
                </div>
                <div class="catalog-table-col value-col" @click="handleSelectClickSnapshot($event, snapshot)">
                  {{ snapshot.catalogGroups }}
                </div>
                <div class="catalog-table-col value-col" @click="handleSelectClickSnapshot($event, snapshot)">
                  {{ this.$d(snapshot.updatedOn, "shortDate") }}
                </div>
                <div class="catalog-table-col value-col" @click="handleSelectClickSnapshot($event, snapshot)">
                  {{ snapshot.updatedBy }}
                </div>
                <div class="catalog-table-col value-col" @click="handleSelectClickSnapshot($event, snapshot)">
                  {{ this.$d(snapshot.createdOn, "shortDate") }}
                </div>
                <div class="catalog-table-col value-col" @click="handleSelectClickSnapshot($event, snapshot)">
                  {{ snapshot.createdBy }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <b-overlay z-index="20" opacity="0.4" no-wrap :show="loading">
        <template #overlay>
          <spinner data-cy="manage-catalog-modal-spinner" />
        </template>
      </b-overlay>
    </template>
    <template #modal-footer="{ ok, cancel }">
      <b-btn variant="primary" :disabled="selectedCount != 1 || isASnapshotInSelection" @click="ok">{{ $t("Open") }}</b-btn>
      <b-btn @click="cancel" data-cy="channel-creator-manage-catalogs-close-btn">{{ $t("close") }}</b-btn>
    </template>
  </b-modal>
</template>

<script>
import { mapState, mapActions } from "vuex";

import _ from "lodash";

import catalogApi from "../../services/catalogs";
import activityApi from "../../services/activity";
import apps from "../../services/applications";

import Spinner from "../../common/components/Spinner.vue";
import RenameCatalogDialog from "./RenameCatalogDialog.vue";
import VersioningCommentModal from "./VersioningCommentModal.vue";
import { useToast } from "@common/composable/toast";
import { naturalSort } from "@util/naturalSort";

export default {
  components: { Spinner, RenameCatalogDialog, VersioningCommentModal },
  props: ["showDialog", "includedCatalogTypes", "excludeCatalogsById", "dialogMode", "modalName", "filterAllButThisIds"],
  setup() {
    const { createToast } = useToast();

    return { createToast };
  },
  data() {
    return {
      loading: false,
      fetchedData: [],

      searchTerm: "",
      sortBy: { name: "category", desc: false },
      snapShots: null,
      selectAll: false,
      isOpen: false
    };
  },
  computed: {
    ...mapState("metadata", ["contentLanguages"]),
    ...mapState("channelCreator", ["catalogs"]),
    ...mapState(["meta", "globalParameters"]),
    computedTitle() {
      return this.dialogMode == "manage" ? this.$t("ManageCatalogs") : this.$t("OpenCatalog");
    },
    languageCode() {
      return this.contentLanguages?.enabled[0]?.languageCode;
    },
    allSnapshots() {
      return _.flatMap(this.snapShots, (x) => {
        return x.snapshots;
      });
    },
    selectedCount() {
      const me = this;
      return (me.selectedCatalogIds?.length ?? 0) + (me.allSnapshots?.filter((x) => x.selected).length ?? 0);
    },
    isASnapshotInSelection() {
      return _.some(this.allSnapshots, (x) => x.selected);
    },
    isBaseCatalogSelected() {
      return this.fetchedData?.filter((x) => x.selected).some((y) => y.catalogType == 0);
    },
    sortedList() {
      let filteredData = [];
      if (this.filterAllButThisIds && this.filterAllButThisIds.length) {
        filteredData = _.filter(this.fetchedData, (x) => _.includes(this.filterAllButThisIds, x.catalogId));
        filteredData = !this.searchTerm
          ? filteredData
          : _.filter(filteredData, (x) => x.name && x.name.toLowerCase().indexOf(this.searchTerm.toLowerCase()) >= 0);
      } else {
        filteredData = !this.searchTerm
          ? this.fetchedData
          : _.filter(this.fetchedData, (x) => x.name && x.name.toLowerCase().indexOf(this.searchTerm.toLowerCase()) >= 0);
      }

      if (this.sortBy.name == "category") {
        filteredData = naturalSort(filteredData, "name", this.sortBy.desc);
        filteredData = naturalSort(filteredData, "category", this.sortBy.desc);
      } else {
        filteredData = naturalSort(filteredData, this.sortBy.name, this.sortBy.desc);
      }
      return filteredData;
    },
    isDeleteDisabled() {
      return this.selectedCount == 0 || this.isBaseCatalogSelected;
    },
    isCopyDisabled() {
      return this.selectedCount != 1 || this.isASnapshotInSelection;
    },
    isCopySnapshotDisabled() {
      return this.selectedCount != 1 || !this.isASnapshotInSelection;
    },
    isSnapshotDisabled() {
      return this.selectedCount != 1 || this.isASnapshotInSelection || this.isBaseCatalogSelected;
    },
    isRestoreDisabled() {
      return this.selectedCount != 1 || !this.isASnapshotInSelection || this.isBaseCatalogSelected;
    },
    isRestoreAsCopyDisabled() {
      return this.selectedCount != 1 || !this.isASnapshotInSelection;
    },
    isRenameDisabled() {
      return this.selectedCount != 1 || this.isASnapshotInSelection || this.isBaseCatalogSelected;
    },
    selectedCatalogIds() {
      return this.fetchedData?.filter((x) => x.selected).map((x) => x.catalogId);
    },
    selectedCatalogNames() {
      return this.fetchedData?.filter((x) => x.selected).map((x) => x.name);
    },
    selectedSnapshotIds() {
      const me = this;
      return me.allSnapshots?.filter((x) => x.selected).map((x) => x.catalogId);
    },
    selectedSingleCatalog() {
      return this.fetchedData && this.selectedCount == 1 && _.some(this.fetchedData, (x) => x.selected) ? this.fetchedData.find((x) => x.selected) : null;
    },
    selectedSingleSnapshot() {
      return this.allSnapshots && this.selectedCount == 1 && _.some(this.allSnapshots, (x) => x.selected) ? this.allSnapshots.find((x) => x.selected) : null;
    },
    selectedSingleCatalogId() {
      return this.selectedSingleCatalog ? this.selectedSingleCatalog.catalogId : null;
    },
    multiLangCatalogModel() {
      return this.catalogs?.find((x) => x[this.languageCode].catalogId == this.selectedSingleCatalogId) ?? null;
    }
  },
  methods: {
    ...mapActions("channelCreator", ["loadCatalogs"]),
    fetchData() {
      const me = this;
      me.loading = true;

      const promiseArray = [];

      promiseArray.push(
        catalogApi.getCatalogs({
          language: me.languageCode,
          catalogTypes: me.includedCatalogTypes
        })
      );

      Promise.all(promiseArray)
        .then((results) => {
          const prevSelectedCatalogIds = me.selectedCatalogIds ? [...me.selectedCatalogIds] : [];
          const prevOpenCatalogIds = me.fetchedData ? me.fetchedData.filter((x) => x.isShowingCollapsedSnapshots).map((x) => x.catalogId) : [];
          let completeData = [];

          _.forEach(results, (result) => {
            completeData = _.concat(completeData, result);
          });
          me.fetchedData = _.filter(completeData, (x) => !_.includes(this.excludeCatalogsById, x.catalogId));
          _.each(me.fetchedData, (catalog_i) => {
            catalog_i["selected"] = prevSelectedCatalogIds.includes(catalog_i.catalogId);
            catalog_i["isShowingCollapsedSnapshots"] = prevOpenCatalogIds.includes(catalog_i.catalogId);
          });
          if (me.dialogMode == "manage") {
            catalogApi
              .getSnapshotHistoryMultiple(
                me.fetchedData.map((x) => x.catalogId),
                me.languageCode
              )
              .then((result) => {
                _.each(result, (snapshots) => {
                  _.each(snapshots.snapshots, (snapshot) => {
                    snapshot["selected"] = false;
                  });
                });
                me.snapShots = result;
              });
          }
        })
        .finally(() => {
          me.emptySearchTerm();
          me.loading = false;
        });
    },

    normalizeIdString(idString) {
      if (idString) return idString.replace(/\s+/g, "-").toLowerCase();
      else return "";
    },
    handleSelectCatalog(catalog, setValue = null) {
      catalog.selected = setValue ?? !catalog.selected;
    },
    handleSelectClickCatalog(event, catalog) {
      const me = this;
      me.deselectAllSnapshots();

      if (catalog.selected && me.selectedCatalogIds.length == 1) {
        catalog.selected = false;
      } else {
        if (event.ctrlKey) catalog.selected = !catalog.selected;
        else if (event.shiftKey && me.selectedCatalogIds.length > 0) {
          const startCatalog = me.sortedList.filter((x) => x.selected)[0];
          const startCatalogIndex = me.sortedList.indexOf(startCatalog);
          const endCatalogIndex = me.sortedList.indexOf(catalog);
          let start = 0;
          let end = 0;
          if (startCatalogIndex < endCatalogIndex) {
            start = startCatalogIndex;
            end = endCatalogIndex;
          } else {
            start = endCatalogIndex;
            end = startCatalogIndex;
          }

          for (let i = start; i <= end; i++) {
            me.sortedList[i].selected = true;
          }
        } else {
          _.each(me.fetchedData, (catalog_i) => {
            catalog_i.selected = catalog_i == catalog;
          });
        }
      }
      window.getSelection().empty();
    },
    handleSelectClickSnapshot(event, snapshot) {
      const me = this;
      me.deselectAllCatalogs();
      if (snapshot.selected) {
        snapshot.selected = false;
      } else {
        _.each(me.allSnapshots, (snapshot_i) => {
          snapshot_i.selected = snapshot.catalogId == snapshot_i.catalogId;
        });
      }
    },
    deselectAllCatalogs() {
      const me = this;
      if (me.selectedCount || _.some(me.fetchedData, (x) => x.selected)) {
        _.each(me.fetchedData, (catalog) => {
          catalog.selected = false;
        });
      }
    },
    deselectAllSnapshots() {
      const me = this;
      if (me.selectedCount || _.some(me.allSnapshots, (x) => x.selected)) {
        _.each(me.allSnapshots, (snapshot_i) => {
          snapshot_i.selected = false;
        });
      }
    },
    emptySearchTerm() {
      this.searchTerm = null;
    },
    successToast(title, text, icon) {
      this.createToast({
        text: text,
        title: title,
        variant: "success",
        textIllustration: icon,
        statusTextIcon: "CheckCircleSolid",
        titleIcon: "Channel-Creator"
      });
    },
    errorToast(title, text, icon) {
      this.createToast({
        text: text,
        title: title,
        variant: "danger",
        textIllustration: icon,
        statusTextIcon: "XCircleSolid",
        titleIcon: "Channel-Creator"
      });
    },
    handleMultipleDelete() {
      const me = this;
      const tobeDeletedIds = me.selectedCatalogIds.length ? me.selectedCatalogIds : me.selectedSnapshotIds.length ? me.selectedSnapshotIds : [];
      const deleteCount = tobeDeletedIds.length;
      if (deleteCount && confirm(deleteCount > 1 ? me.$t("ConfirmMultipleDelete", { deleteCount }) : me.$t("ConfirmDelete"))) {
        catalogApi
          .deleteCatalogs(tobeDeletedIds)
          .then(() => {
            me.catalogsHaveChanged(null, true);
            me.successToast(me.$t("delete"), me.$t("SuccessDeletingMultiple", { deleteCount: deleteCount }), "Delete");
            me.createActivityEntry();
          })
          .catch(() => {
            me.errorToast(me.$t("delete"), me.$t("ErrorDelete"), "Delete");
          });
      }
    },
    createActivityEntry: async function () {
      try {
        if (this.globalParameters.IsActivityFeedEnabled) {
          const activityCriterias = [];
          this.selectedCatalogNames.forEach((name) => {
            activityCriterias.push({
              severity: "Information",
              applicationId: apps.Modules.ChannelCreator.Id,
              process: "Delete",
              objectType: "Catalog",
              objectValue: !name ? "" : name,
              status: "Successful",
              action: "Completed",
              descriptionKey: "",
              descriptionValues: []
            });
          });
          await activityApi.createActivity(activityCriterias);
        }
      } catch (error) {
        console.error("Activity insertion for catalog delete process failed with error : ", error);
      }
    },
    catalogsHaveChanged(newSelectCatalogId, setToFirstEntry) {
      this.$emit("catalogsChanged", newSelectCatalogId, setToFirstEntry);
      this.fetchData();
    },
    handleModalHide() {
      const me = this;
      me.fetchedData = null;
      me.emptySearchTerm();
    },
    setSortBy(sortByName) {
      const me = this;
      if (me.sortBy.name != sortByName) {
        me.sortBy = { name: sortByName, desc: false };
      } else {
        me.sortBy.desc = !me.sortBy.desc;
      }
    },
    hasSnapshot(catalogId) {
      if (this.snapShots) return !!this.snapShots.find((x) => x.catalogId == catalogId);
      else return false;
    },
    sortSnapshots(snapshotList) {
      if (snapshotList) {
        return _.sortBy(snapshotList.snapshots, "createdOn");
      }
      return [];
    },
    copyCatalog() {
      const me = this;
      if (me.selectedCount == 1) {
        catalogApi
          .copyCatalog(me.selectedCatalogIds[0], me.languageCode)
          .then(() => {
            me.catalogsHaveChanged(me.selectedCatalogIds[0]);
            me.successToast(me.$t("Copy", me.$t("SuccessCopy")), "CopyCatalog");
          })
          .catch(() => {
            me.errorToast(me.$t("Copy"), me.$t("ErrorCopy"), "CopyCatalog");
          });
      }
    },
    restoreSnapShot() {
      const me = this;
      const snapshotDate = me.$d(me.selectedSingleSnapshot.updatedOn, "shortDateTime");
      if (me.selectedSnapshotIds.length == 1 && confirm(me.$t("ConfirmRestoreSnapshot", { snapShotDate: snapshotDate }))) {
        catalogApi
          .restoreSnapShot(me.selectedSnapshotIds[0], me.languageCode)
          .then(() => {
            me.catalogsHaveChanged(me.selectedSnapshotIds[0]);
            me.successToast(me.$t("VersionRestored"), me.$t("SuccessRestored", { snapShotDate: snapshotDate }), "Restore");
          })
          .catch(() => {
            me.errorToast(this.$t("VersionRestored"), this.$t("ErrorRestored"), "Restore");
          });
      }
    },
    restorAsCopySnapShot() {
      const me = this;
      const snapshotDate = me.$d(me.selectedSingleSnapshot.updatedOn, "shortDateTime");
      if (me.selectedSnapshotIds.length == 1 && confirm(me.$t("ConfirmRestoreSnapshotAsCopy", { snapShotDate: snapshotDate }))) {
        catalogApi
          .copySnapShot(me.selectedSnapshotIds[0], me.languageCode)
          .then((result) => {
            me.catalogsHaveChanged(result.catalogId);
            me.successToast(me.$t("VersionRestored"), me.$t("SuccessAsCopyRestored", { snapShotDate: snapshotDate }), "Restore");
          })
          .catch(() => {
            me.errorToast(this.$t("VersionRestored"), this.$t("ErrorRestored"), "Restore");
          });
      }
    },
    handleCreationOfSnapshot_OK() {
      const me = this;
      me.successToast(
        me.$t("CatalogVersioning"),
        me.$t("SnapshotTaken", {
          catalogName: this.selectedSingleCatalog.name
        }),
        "SnapshotCreate"
      );
      me.catalogsHaveChanged();
    },
    handleCreationOfSnapshot_Error() {
      const me = this;
      me.errorToast(
        me.$t("CatalogVersioning"),
        me.$t("SnapshotCouldNotBeTaken", {
          catalogName: this.selectedSingleCatalog.name
        }),
        "SnapshotCreate"
      );
    },
    openCatalog() {
      this.$emit("selectedCatalog", this.selectedCatalogIds[0]);
      this.$emit("update:showDialog", false);
    },
    openDblClickCatalog(catalogId, closeFunction) {
      const me = this;
      me.$emit("selectedCatalog", catalogId);
      this.$emit("update:showDialog", false);
      closeFunction();
    },
    hideDialog() {
      this.$emit("update:showDialog", false);
    }
  },
  watch: {
    selectAll(nv) {
      const me = this;
      _.each(this.sortedList, (catalog) => {
        me.handleSelectCatalog(catalog, nv);
      });
    },
    showDialog(nv) {
      this.isOpen = nv;
    }
  }
};
</script>
